import { getStagePath } from './stageRoutes';

export const USER_DATA_KEY = 'userData';
export const ASSIGNMENT_SLOT_MAX = 10;

function buildActiveStagesFromSlots(source) {
  if (!source || typeof source !== 'object') return [];
  const stages = [];
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const n = Number(source[`s${slot}`]);
    if (Number.isFinite(n) && n >= 1) stages.push(n);
  }
  return stages;
}

function slotFieldsFromGame(game, fallback = {}) {
  const out = {};
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const key = `s${slot}`;
    out[key] = game?.[key] ?? fallback[key] ?? null;
  }
  return out;
}

function hasAssignedSlot(game, slot) {
  const stageNum = Number(game?.[`s${slot}`]);
  return Number.isFinite(stageNum) && stageNum >= 1;
}

export function readStoredUserData() {
  try {
    const raw = sessionStorage.getItem(USER_DATA_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Normalize `initStageReport` response `{ game: { stages } }` into assignment shape. */
export function gameFromInitStageReport(initResult) {
  const g = initResult?.game;
  if (!g || typeof g !== 'object') return null;
  const stages = g.stages ?? g;
  if (!stages || typeof stages !== 'object') return null;
  return {
    id: g.id ?? stages.id ?? null,
    puzzle_id: stages.puzzle_id != null ? Number(stages.puzzle_id) : null,
    ...slotFieldsFromGame(stages),
    activeStages: Array.isArray(g.activeStages)
      ? g.activeStages.map(Number)
      : buildActiveStagesFromSlots(stages),
  };
}

export function applyGameAssignmentToUserData(userData, game) {
  if (!userData || !game) return userData;
  const puzzleId = game.puzzle_id ?? userData.puzzle_id ?? userData.puzzleid ?? null;
  return {
    ...userData,
    ...slotFieldsFromGame(game, userData),
    puzzle_id: puzzleId,
    puzzleid: puzzleId,
    gameStages: game,
    activeStages: game.activeStages ?? buildActiveStagesFromSlots(game),
  };
}

export function getGameAssignment(source) {
  if (!source || typeof source !== 'object') return null;
  if (source.gameStages && typeof source.gameStages === 'object') return source.gameStages;
  const hasSlot = Array.from({ length: ASSIGNMENT_SLOT_MAX }, (_, i) => `s${i + 1}`).some(
    (key) => source[key] != null
  );
  if (hasSlot) {
    return {
      id: source.id ?? null,
      puzzle_id: source.puzzle_id ?? source.puzzleid ?? null,
      ...slotFieldsFromGame(source),
      activeStages: source.activeStages ?? buildActiveStagesFromSlots(source),
    };
  }
  return null;
}

const DEFAULT_ASSIGNMENT_STAGE_COUNT = 3;

/** Count s1–s10 slots with a valid assigned stage number. */
export function countAssignedStages(game) {
  if (!game || typeof game !== 'object') return DEFAULT_ASSIGNMENT_STAGE_COUNT;

  let count = 0;
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    if (hasAssignedSlot(game, slot)) count += 1;
  }

  return count > 0 ? count : DEFAULT_ASSIGNMENT_STAGE_COUNT;
}

export function mergeGameAssignmentIntoUserData({ game, currentStage, nextDay, report }) {
  const stored = readStoredUserData();
  const puzzleId = game?.puzzle_id ?? stored.puzzle_id ?? stored.puzzleid ?? null;
  const merged = {
    ...stored,
    ...slotFieldsFromGame(game, stored),
    puzzle_id: puzzleId,
    puzzleid: puzzleId,
    gameStages: game ?? stored.gameStages ?? null,
    activeStages: game?.activeStages ?? stored.activeStages ?? buildActiveStagesFromSlots(game ?? stored),
    currentStage: currentStage ?? stored.currentStage ?? null,
    nextDay: nextDay ?? stored.nextDay ?? null,
    ...(report && typeof report === 'object' ? { report } : {}),
  };
  sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
  return merged;
}

export function resolveStageForDay(dayNumber, gameAssignment) {
  const day = Number(dayNumber);
  if (!Number.isFinite(day) || day < 1) return null;
  const g = gameAssignment || {};
  if (day >= 1 && day <= ASSIGNMENT_SLOT_MAX) {
    const slot = Number(g[`s${day}`]);
    if (Number.isFinite(slot) && slot >= 1) return slot;
  }
  return null;
}

export function resolveNextStageAfterDay(completedDay, gameAssignment) {
  const day = Number(completedDay);
  if (!Number.isFinite(day) || day < 1) return null;
  return resolveStageForDay(day + 1, gameAssignment);
}

export function isAssignmentStagesComplete(completedDay, game) {
  const day = Number(completedDay);
  const total = countAssignedStages(game);
  return Number.isFinite(day) && day >= total;
}

/** Which assignment slot (1–10) maps to this stage route, or null if not assigned. */
export function getAssignmentSlotForStage(stageNumber, gameAssignment) {
  const stage = Number(stageNumber);
  if (!Number.isFinite(stage) || !gameAssignment) return null;
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    if (Number(gameAssignment[`s${slot}`]) === stage) return slot;
  }
  return null;
}

/** True when submitting this stage completes the last assigned slot. */
export function isAssignmentCompleteAfterStage(stageNumber, gameAssignment) {
  const slot = getAssignmentSlotForStage(stageNumber, gameAssignment);
  if (slot == null) return false;
  return slot === countAssignedStages(gameAssignment);
}

const POST_ASSIGNMENT_STAGE_MAX = 10;

/** Ordered play sequence: all assignment slots, then puzzle. */
export function buildPlayPathSequence(game) {
  const sequence = [];
  if (game) {
    for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
      const stageNum = Number(game[`s${slot}`]);
      if (Number.isFinite(stageNum) && stageNum >= 1) {
        sequence.push({ type: 'stage', stageNum });
      }
    }
  }
  sequence.push({ type: 'puzzle' });
  return sequence;
}

function playSegmentToPath(segment) {
  if (segment.type === 'puzzle') return '/puzzle';
  return getStagePath(segment.stageNum);
}

function isPlaySegmentPlayed(segment, report, game) {
  if (segment.type === 'puzzle') {
    return areAssignmentRoundsComplete(report, game);
  }
  return hasStageAnswerInReport(report, segment.stageNum);
}

function getCurrentPlayIndex(sequence, { stageNumber, isPuzzle }) {
  if (isPuzzle) {
    return sequence.findIndex((segment) => segment.type === 'puzzle');
  }
  const stage = Number(stageNumber);
  let index = -1;
  for (let i = 0; i < sequence.length; i += 1) {
    const segment = sequence[i];
    if (segment.type === 'stage' && segment.stageNum === stage) {
      index = i;
    }
  }
  return index;
}

/** Back target from a stage route — last completed step before the current one. */
export function getPreviousPlayedStagePath(currentStageNumber, report, game) {
  const sequence = buildPlayPathSequence(game);
  const currentIndex = getCurrentPlayIndex(sequence, {
    stageNumber: currentStageNumber,
    isPuzzle: false,
  });
  if (currentIndex <= 0) return null;

  for (let i = currentIndex - 1; i >= 0; i -= 1) {
    if (isPlaySegmentPlayed(sequence[i], report, game)) {
      return playSegmentToPath(sequence[i]);
    }
  }
  return null;
}

/** Back target from puzzle — last completed assignment stage. */
export function getPreviousPlayedPuzzlePath(report, game) {
  if (!game) return null;
  for (let slot = ASSIGNMENT_SLOT_MAX; slot >= 1; slot -= 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (Number.isFinite(stageNum) && stageNum >= 1 && hasStageAnswerInReport(report, stageNum)) {
      return getStagePath(stageNum);
    }
  }
  return null;
}

/** Header Back target: previous stage in assigned play order. */
export function getPreviousStagePath(currentStageNumber, game) {
  const current = Number(currentStageNumber);
  if (!Number.isFinite(current) || current < 1 || current > POST_ASSIGNMENT_STAGE_MAX) {
    return '/rules';
  }

  const slot = getAssignmentSlotForStage(current, game);
  if (slot != null && game) {
    if (slot > 1) {
      const prevStage = Number(game[`s${slot - 1}`]);
      if (Number.isFinite(prevStage) && prevStage >= 1) {
        return getStagePath(prevStage);
      }
    }
    return '/rules';
  }

  return '/rules';
}

/** Header Back from puzzle → last assignment stage, or rules. */
export function getPuzzleBackPath(game) {
  if (!game) return '/rules';
  for (let slot = ASSIGNMENT_SLOT_MAX; slot >= 1; slot -= 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (Number.isFinite(stageNum) && stageNum >= 1) return getStagePath(stageNum);
  }
  return '/rules';
}

/** Map stage route (1–10) to stages table day column (dayN_score / dayN_answer). */
export function getProgressDayForStage(stageNumber) {
  const n = Number(stageNumber);
  if (!Number.isFinite(n) || n < 1 || n > POST_ASSIGNMENT_STAGE_MAX) return null;
  return n;
}

/** True when stages.dayN_answer (or dayN_score) indicates this stage was completed. */
export function hasStageAnswerInReport(report, stageNumber) {
  if (!report) return false;
  const day = getProgressDayForStage(stageNumber);
  if (!day) return false;

  const answerRaw = report[`day${day}_answer`];
  const answerText = String(answerRaw ?? '').trim();
  if (answerText && answerText !== 'null' && answerText !== 'undefined') {
    return true;
  }

  const score = Number(report[`day${day}_score`]);
  return Number.isFinite(score) && score > 0;
}

/** True when every assigned s1–s10 slot has an answer in the report. */
export function areAssignmentRoundsComplete(report, game) {
  if (!game || !report) return false;
  let hasSlot = false;
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (!Number.isFinite(stageNum) || stageNum < 1) continue;
    hasSlot = true;
    if (!hasStageAnswerInReport(report, stageNum)) return false;
  }
  return hasSlot;
}

/** Redirect from user's game row: first unplayed assignment slot, else puzzle. */
export function resolveNavigatePathFromGameAssignment(game, report) {
  return getNavigatePathFromAllAssignmentSlots(game, report) ?? '/puzzle';
}

/** First stage route that still needs to be played, or /puzzle when done. */
export function getFirstUnplayedStagePath(report, game) {
  return resolveNavigatePathFromGameAssignment(game, report);
}

/** First unplayed stage in assignment slots, or null if all assigned slots are done. */
export function getNavigatePathFromAllAssignmentSlots(game, report) {
  if (!game) return null;

  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (!Number.isFinite(stageNum) || stageNum < 1) continue;
    if (!hasStageAnswerInReport(report, stageNum)) {
      return getStagePath(stageNum);
    }
  }

  return null;
}

/** Remaining assignment slots after the first three, or /puzzle. */
export function getNavigatePathFromExtraAssignmentSlots(game, report) {
  if (!game) return '/puzzle';

  for (let slot = 4; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (!Number.isFinite(stageNum) || stageNum < 1) continue;
    if (!hasStageAnswerInReport(report, stageNum)) {
      return getStagePath(stageNum);
    }
  }

  return '/puzzle';
}

/** After stage 9: play any remaining s1–s5 assigned stages, otherwise puzzle. */
export function getNavigatePathAfterStage9Complete(game, report) {
  return getNavigatePathFromAllAssignmentSlots(game, report) ?? '/puzzle';
}

/** Navigate after submit or already-played — checks game s1–s5, then puzzle. */
export function resolveNavigatePathFromProgress(stageNumber, stageData, report) {
  const stage = Number(stageNumber);
  const game = stageData?.game ?? getGameAssignment(stageData) ?? null;
  let path = resolveNavigatePathFromGameAssignment(game, report);

  if (Number.isFinite(stage) && path === getStagePath(stage)) {
    const slot = getAssignmentSlotForStage(stage, game);
    if (slot != null && game) {
      for (let s = slot + 1; s <= ASSIGNMENT_SLOT_MAX; s += 1) {
        const nextAssigned = Number(game[`s${s}`]);
        if (Number.isFinite(nextAssigned) && nextAssigned >= 1) {
          if (!hasStageAnswerInReport(report, nextAssigned)) {
            return getStagePath(nextAssigned);
          }
        }
      }
    }
    return '/puzzle';
  }

  return path;
}

/** Navigate target after completing assignment day `completedDay` (from getGameStages nextDay). */
export function getNavigatePathAfterStageComplete(completedDay, game) {
  const day = Number(completedDay);
  if (!Number.isFinite(day) || day < 1) return null;
  if (isAssignmentStagesComplete(day, game)) return '/puzzle';
  const nextStage = resolveNextStageAfterDay(day, game);
  if (Number.isFinite(nextStage) && nextStage >= 1) return getStagePath(nextStage);
  return null;
}

/** Navigate target from a fresh getGameStages response (after progress save). */
export function getNavigatePathFromStageData(stageData) {
  const game = stageData?.game ?? null;
  const report = stageData?.report ?? null;
  const fromAssignment = resolveNavigatePathFromGameAssignment(game, report);
  if (fromAssignment !== '/puzzle') return fromAssignment;

  const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
  if (nextDay != null && nextDay >= 1 && nextDay <= ASSIGNMENT_SLOT_MAX && game) {
    const stage = Number(game[`s${nextDay}`]);
    if (Number.isFinite(stage) && stage >= 1) return getStagePath(stage);
  }

  return '/puzzle';
}

/** After completing or re-visiting a stage — never return the same stage route. */
export function resolveNavigatePathAfterStageSubmit(stageNumber, stageData, report = null) {
  if (report) {
    return resolveNavigatePathFromProgress(stageNumber, stageData, report);
  }

  const stage = Number(stageNumber);
  let path = getNavigatePathFromStageData(stageData) || '/puzzle';

  if (!Number.isFinite(stage) || path !== getStagePath(stage)) {
    return path;
  }

  return '/puzzle';
}

/** Count how many assignment slots have answers in the report. */
export function countCompletedAssignmentSlots(report, game) {
  if (!game || !report) return 0;
  let completed = 0;
  for (let slot = 1; slot <= ASSIGNMENT_SLOT_MAX; slot += 1) {
    const stageNum = Number(game[`s${slot}`]);
    if (!Number.isFinite(stageNum) || stageNum < 1) continue;
    if (hasStageAnswerInReport(report, stageNum)) completed += 1;
  }
  return completed;
}

export function getCompletedDaysForReveal(stageData, report, completedStageNumber) {
  const game = stageData?.game ?? getGameAssignment(stageData) ?? null;
  const totalAssigned = countAssignedStages(game);

  if (game && report) {
    let completed = countCompletedAssignmentSlots(report, game);
    const stage = Number(completedStageNumber);
    if (
      Number.isFinite(stage) &&
      getAssignmentSlotForStage(stage, game) != null &&
      !hasStageAnswerInReport(report, stage)
    ) {
      completed += 1;
    }
    if (completed > 0) {
      return Math.max(1, Math.min(totalAssigned, completed));
    }
  }

  const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
  if (nextDay == null || !Number.isFinite(nextDay)) {
    return totalAssigned > 0 ? totalAssigned : 1;
  }
  return Math.max(0, Math.min(totalAssigned, nextDay - 1));
}

export async function resolveNavigatePathAfterSubmit({
  completedDay,
  game,
  stageNumber,
  refreshStageData,
}) {
  let path = getNavigatePathAfterStageComplete(completedDay, game);
  const stage = Number(stageNumber);
  if (!path || (Number.isFinite(stage) && path === getStagePath(stage))) {
    const refreshed = await refreshStageData();
    path = getNavigatePathFromStageData(refreshed);
  }
  return path;
}
